import { Chord, Interval, Note } from 'tonal';
import { chromaOf } from './fretboard';
import type { ChordQuality } from './harmony';

/**
 * Chord construction and spelling (topic C5): triads, then the four common
 * sevenths, then sus and add chords, all spelled by tonal. The kinds and their
 * formulas live here so drills (D7 spelling, D8 chord tones) and skills share
 * one vocabulary. `harmony.ts` keeps its own `ChordQuality` for diatonic work;
 * `fromHarmonyQuality` maps between the two.
 */

export type ChordKind =
  | 'major'
  | 'minor'
  | 'diminished'
  | 'augmented'
  | 'maj7'
  | '7'
  | 'm7'
  | 'm7b5'
  | 'dim7'
  | 'sus2'
  | 'sus4'
  | 'add9';

/** Teaching order within C5: triads → sevenths → sus/add. */
export type ChordFamily = 'triad' | 'seventh' | 'suspended' | 'added';

/** A formula degree, relative to the chord root. */
export type ChordDegree =
  '1' | '2' | '3' | 'b3' | '4' | '5' | 'b5' | '#5' | '7' | 'b7' | 'bb7' | '9';

export interface ChordKindInfo {
  readonly kind: ChordKind;
  readonly family: ChordFamily;
  /** Symbol suffix as charts write it: `` (C), `m7b5` (Bm7b5). */
  readonly suffix: string;
  /** Spoken name, e.g. `half-diminished seventh`. */
  readonly name: string;
  /** Formula, root first: 1 3 5 b7. */
  readonly formula: readonly ChordDegree[];
  /** tonal chord type used for spelling. */
  readonly tonalType: string;
}

                                                      
   
                  
                    
               
                  
                             
                   
    
   
                  
                    
                
                  
                              
                   
    
   
                       
                    
                  
                       
                               
                     
    
   
                      
                    
                  
                      
                              
                     
    
   
                 
                      
                   
                          
                                  
                      
    
   
              
                      
                
                             
                                   
                   
    
   
               
                      
                 
                          
                                    
                    
    
   
                 
                      
                   
                                    
                                     
                      
    
   
                 
                      
                   
                               
                                      
                      
    
   
                 
                        
                   
                             
                             
                      
    
   
                 
                        
                   
                             
                             
                      
    
   
                 
                    
                   
                        
                                  
                      
    
  

/** The kinds C5 names explicitly: major/minor triads, maj7/7/m7/m7b5, sus2/sus4, add9. */
                                                     
          
          
         
      
       
         
         
         
         
  

/**
 * Pairs learners mix up (research/03 C5): the b7 of `7` vs the 7 of `maj7`,
 * `m7` vs `m7b5`, and the two sus chords.
 */
                                                                                     
                
                 
              
                   
                     
                   
  

const BY_KIND = new Map(CHORD_KINDS.map((k) => [k.kind, k]));

                                                      
            
            
            
           
            
            
           
             
            
           
            
            
  

/** Extra suffixes accepted by `parseChord`, beyond each kind's own. */
                                                   
             
               
               
               
                    
                  
                   
             
            
             
            
             
             
            
             
                 
               
             
              
               
  

                                                               
                                 
                                                                      
              
 

                                                                
                                         
 

                                       
                           
                                                                                     
              
 

/** Chord symbol: `nameChord('B', 'm7')` → `Bm7`. */
                                                                  
                                                        
 

export interface ParsedChord {
  readonly root: string;
  readonly kind: ChordKind;
}

/** `Bm7`, `F#m7b5`, `Bø`, `Csus`, `Eadd9`, `GΔ7` → root and kind. Throws on anything else. */
                                                         
                                                                  
                                           
                                                                                            
                                                                                   
                                                
 

/**
 * The chord's notes as pitch classes in formula order, spelled by tonal:
 * Bm7 → B D F# A; Gbdim → Gb Bbb Dbb (strict spelling keeps the letters).
 */
                                                                     
                                                                            
                     
 

/** `spellChord` from a symbol: `chordNotes('Bm7')` → B D F# A. */
                                                      
                                            
                                
 

                                                                       
                                     
 

/**
 * The note on one formula degree, or `null` if the chord doesn't have it:
 * the 3rd of D7 is F#; Csus4 has no 3.
 */
                                                                                                
                                               
                                                              
 

/** The formula degree a pitch plays in the chord, or `null`: F# in D7 → `3`. */
                                                                                                
                           
                                                                       
                                                               
 

                                                                   
                                                     
 

export interface ChordDifference {
  /** Degrees only the first kind has. */
  readonly onlyA: readonly ChordDegree[];
  /** Degrees only the second kind has. */
  readonly onlyB: readonly ChordDegree[];
}

/**
 * How two kinds differ, for feedback: `7` vs `maj7` → onlyA [b7], onlyB [7].
 */
                                                                              
                             
                             
                                                                                                
 

/** True when the notes (any order, any spelling) are exactly the chord's pitches. */
                                                                                                  
                                                             
                                           
                                                                      
 

/** True when the spelling avoids double sharps and flats (Gbdim fails; F#dim passes). */
                                                                        
                                              
 

                                                       
                 
                 
                           
                         
                 
                 
               
                             
                      
  

/** Map `harmony.ts`'s diatonic qualities onto chord kinds. */
                                                                      
                               
 

import { CHORD_KINDS } from "/Users/pookey/Documents/personal/Projects.nosync.noindex/guitar.nosync/app/src/theory/chords.ts?split=0";
export { BY_KIND };
